import { defineTrpcContract, mutation, query } from "@amtana/module";
import { z } from "zod";

import {
  checkoutSessionSchema,
  portalSessionSchema,
  startCheckoutInputSchema,
} from "./billing-checkout.schemas.ts";
import {
  awakeHoursSchema,
  modelTurnCreditsInputSchema,
  modelTurnCreditsSchema,
  simulateAwakeInputSchema,
} from "./billing-remote-agent.schemas.ts";
import { addSeatsInputSchema, setAutoRechargeInputSchema } from "./billing-seats.schemas.ts";
import {
  billingDetailsSchema,
  billingOverviewSchema,
  cancelInputSchema,
  creditHistorySchema,
  emptyInputSchema,
  featureSchema,
  invoiceDocumentInputSchema,
  invoiceDocumentSchema,
  invoicesSchema,
  offerInputSchema,
  planLimitsSchema,
  simulateInputSchema,
  subscriptionSchema,
} from "./billing.schemas.ts";
import { paymentOptionsInputSchema, paymentOptionsSchema } from "./payment-providers.schemas.ts";

export const billingTrpcContract = defineTrpcContract("billing", {
  overview: query({ input: emptyInputSchema, output: billingOverviewSchema }),
  limits: query({ input: emptyInputSchema, output: planLimitsSchema }),
  hasFeature: query({ input: z.object({ feature: featureSchema }), output: z.boolean() }),
  creditHistory: query({ input: emptyInputSchema, output: creditHistorySchema }),
  simulate: mutation({ input: simulateInputSchema, output: billingOverviewSchema }),
  subscription: query({ input: emptyInputSchema, output: subscriptionSchema }),
  invoices: query({ input: emptyInputSchema, output: invoicesSchema }),
  startCheckout: mutation({ input: startCheckoutInputSchema, output: checkoutSessionSchema }),
  portalLink: mutation({ input: emptyInputSchema, output: portalSessionSchema }),
  paymentOptions: query({ input: paymentOptionsInputSchema, output: paymentOptionsSchema }),
  // The provider's webhook no longer rides tRPC (ADR-012 §6): Stripe signs the raw body with
  // its own `Stripe-Signature` header, not our envelope, so it is a REST door instead
  // (`billingRestTransport`, `POST /api/billing/webhook`).
  cancel: mutation({ input: cancelInputSchema, output: subscriptionSchema }),
  uncancel: mutation({ input: emptyInputSchema, output: subscriptionSchema }),
  acceptOffer: mutation({ input: offerInputSchema, output: subscriptionSchema }),
  unfreeze: mutation({ input: emptyInputSchema, output: subscriptionSchema }),
  addSeats: mutation({ input: addSeatsInputSchema, output: billingOverviewSchema }),
  setAutoRecharge: mutation({ input: setAutoRechargeInputSchema, output: billingOverviewSchema }),
  invoiceDocument: mutation({ input: invoiceDocumentInputSchema, output: invoiceDocumentSchema }),
  setBillingDetails: mutation({ input: billingDetailsSchema, output: billingDetailsSchema }),
  /** The remote agent's awake hours this month (ADR-023 §13). */
  awakeHours: query({ input: emptyInputSchema, output: awakeHoursSchema }),
  /** Development and tests only: this month's used awake minutes, set by hand. */
  simulateAwake: mutation({ input: simulateAwakeInputSchema, output: awakeHoursSchema }),
  /** What each turn on our model drew from the AI credits, from the ledger (D676). */
  modelTurnCredits: query({ input: modelTurnCreditsInputSchema, output: modelTurnCreditsSchema }),
});

// charge() and the ledger-moving operations (spendCredits, grantCredits, setAddon,
// changePlan, setStatus, reportMembers) are peer-only: no customer ever calls them
// directly, so they carry no procedure here.
